import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ScaleIcon from "@mui/icons-material/Scale";
import UndoIcon from "@mui/icons-material/Undo";
import ExcelJS from "exceljs";
import { useDispatch, useSelector } from "react-redux";
import { fetchPmPartWeights, savePmPartWeights } from "../actions/ProductionMonitorAction";

/* -------------------- Part weights of an IWO (Insert Frames) --------------------
   First time for an IWO : upload the part list Excel (Part + Weight columns) -> InsertPartWeights
   After that            : edit a weight -> UpdatePartWeights (changed parts only)
                           re-upload Excel -> new parts InsertPartWeights, changed weights UpdatePartWeights
------------------------------------------------------------------------------------ */

const NAVY = "#142c54";
const BLUE = "#154c79";

const fmtKg = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })} kg`;
const sameWeight = (a, b) => Math.abs(Number(a) - Number(b)) < 0.0005;

// value of an ExcelJS cell (number, text, rich text, formula result, hyperlink)
const cellValue = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") {
    if (v.result !== undefined) return cellValue(v.result);
    if (Array.isArray(v.richText)) return v.richText.map((t) => t.text).join("");
    if (v.text !== undefined) return cellValue(v.text);
    if (v instanceof Date) return v.toISOString();
    return "";
  }
  return v;
};

// Read the part list Excel: only the "Part" and "Weight" columns are used
const readPartExcel = async (file) => {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());

  for (const ws of wb.worksheets) {
    // find the header row (first 15 rows) that has "Part" and "Weight"
    let headerRow = 0;
    let partCol = 0;
    let weightCol = 0;
    for (let r = 1; r <= Math.min(15, ws.rowCount) && !headerRow; r += 1) {
      const texts = (ws.getRow(r).values || []).map((v) => String(cellValue(v)).trim().toLowerCase());
      const p = texts.findIndex((t) => t === "part" || t === "part no" || t === "partno");
      const w = texts.findIndex((t) => t === "weight");
      if (p > 0 && w > 0) {
        headerRow = r;
        partCol = p;
        weightCol = w;
      }
    }
    if (!headerRow) continue;

    const seen = new Map();
    const duplicates = [];
    let skipped = 0;
    for (let r = headerRow + 1; r <= ws.rowCount; r += 1) {
      const row = ws.getRow(r);
      const partNo = String(cellValue(row.getCell(partCol).value)).trim();
      const rawWeight = cellValue(row.getCell(weightCol).value);
      if (!partNo) continue; // empty line
      const weight = typeof rawWeight === "number" ? rawWeight : parseFloat(String(rawWeight).replace(/,/g, ""));
      if (isNaN(weight)) {
        skipped += 1;
        continue;
      }
      if (seen.has(partNo)) {
        duplicates.push(partNo); // same part twice -> first row is used
        continue;
      }
      seen.set(partNo, Math.round(weight * 1000) / 1000);
    }

    return {
      sheet: ws.name,
      parts: Array.from(seen, ([partNo, weight]) => ({ partNo, weight })),
      duplicates: Array.from(new Set(duplicates)),
      skipped,
    };
  }
  throw new Error('No "Part" and "Weight" columns found in this Excel');
};

/* -------------------- Small table used in the dialogs -------------------- */
const th = {
  fontFamily: "inherit",
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "#eef2f7",
  color: "#0E3C66",
  fontWeight: 700,
  fontSize: "12px",
  textAlign: "left",
  padding: "8px",
  whiteSpace: "nowrap",
};
const td = { fontFamily: "inherit", padding: "6px 8px", borderBottom: "1px solid #eef1f5", fontSize: "13px" };

const PreviewList = ({ title, color, rows, showOld }) =>
  rows.length === 0 ? null : (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color, mb: 0.75 }}>
        {title} ({rows.length})
      </Typography>
      <Box sx={{ maxHeight: 260, overflow: "auto", border: "1px solid #eef1f5", borderRadius: "10px" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontFamily: (t) => t.typography.fontFamily }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={th}>Part</Box>
              {showOld && <Box component="th" sx={{ ...th, textAlign: "right" }}>Saved</Box>}
              <Box component="th" sx={{ ...th, textAlign: "right" }}>{showOld ? "Excel" : "Weight"}</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((p) => (
              <Box component="tr" key={p.partNo}>
                <Box component="td" sx={{ ...td, fontWeight: 600 }}>{p.partNo}</Box>
                {showOld && <Box component="td" sx={{ ...td, textAlign: "right", color: "#9aa3b2" }}>{fmtKg(p.oldWeight)}</Box>}
                <Box component="td" sx={{ ...td, textAlign: "right", fontWeight: showOld ? 700 : 400 }}>{fmtKg(p.weight)}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );

/* -------------------- Main -------------------- */
const PartWeights = ({ iwo }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { partWeights, partWeightsLoading, partWeightsError, partWeightsSaving } = useSelector(
    (state) => state.productionMonitor
  );
  const userId = localStorage.getItem("employeeId") || "";
  const iwoNo = iwo ? String(iwo.IwoNo) : "";

  const fileRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState("");
  const [preview, setPreview] = useState(null); // { fileName, sheet, parts, duplicates, skipped, newParts, changedParts, unchanged, missing }
  const [editOpen, setEditOpen] = useState(false);
  const [edits, setEdits] = useState({}); // partNo -> typed weight
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState(null); // { severity, text }
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    setPreview(null);
    setReadError("");
    setMessage(null);
    if (iwoNo) dispatch(fetchPmPartWeights(iwoNo));
  }, [dispatch, iwoNo]);

  const saved = useMemo(() => partWeights || [], [partWeights]);
  const hasParts = saved.length > 0;
  const totalWeight = useMemo(() => saved.reduce((n, p) => n + (Number(p.Weight) || 0), 0), [saved]);

  /* ---- Excel ---- */
  const handleFile = async (file) => {
    if (!file) return;
    setReadError("");
    setMessage(null);
    if (!/\.xlsx$/i.test(file.name)) {
      setReadError("Please choose an .xlsx Excel file (old .xls is not supported - save it as .xlsx)");
      return;
    }
    setReading(true);
    try {
      const data = await readPartExcel(file);
      if (data.parts.length === 0) throw new Error("No parts with a weight found in this Excel");

      // compare with what is already saved for this IWO
      const savedMap = new Map(saved.map((p) => [String(p.PartNo), Number(p.Weight)]));
      const newParts = [];
      const changedParts = [];
      let unchanged = 0;
      data.parts.forEach((p) => {
        if (!savedMap.has(p.partNo)) newParts.push(p);
        else if (!sameWeight(savedMap.get(p.partNo), p.weight)) changedParts.push({ ...p, oldWeight: savedMap.get(p.partNo) });
        else unchanged += 1;
      });
      const excelSet = new Set(data.parts.map((p) => p.partNo));
      const missing = saved.filter((p) => !excelSet.has(String(p.PartNo))).length;

      setPreview({ fileName: file.name, ...data, newParts, changedParts, unchanged, missing });
    } catch (err) {
      setReadError(err.message || "Could not read this Excel");
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = ""; // same file can be chosen again
    }
  };

  const savePreview = async () => {
    if (!preview) return;
    const result = await dispatch(
      savePmPartWeights({
        iwoNo,
        newParts: preview.newParts.map(({ partNo, weight }) => ({ partNo, weight })),
        changedParts: preview.changedParts.map(({ partNo, weight }) => ({ partNo, weight })),
        userId,
      })
    );
    if (result.success) {
      setPreview(null);
      setMessage({
        severity: "success",
        text: `Part weights saved for IWO ${iwoNo}: ${result.inserted} added, ${result.updated} updated`,
      });
    } else {
      setMessage({ severity: "error", text: result.message });
    }
    dispatch(fetchPmPartWeights(iwoNo));
  };

  /* ---- Edit ---- */
  const openEdit = () => {
    setEdits({});
    setSearch("");
    setEditOpen(true);
  };
  const changedEdits = saved
    .filter((p) => edits[p.PartNo] !== undefined && edits[p.PartNo] !== "" && !sameWeight(edits[p.PartNo], p.Weight))
    .map((p) => ({ partNo: String(p.PartNo), weight: Number(edits[p.PartNo]) }));
  const badEdit = Object.values(edits).some((v) => v !== "" && (isNaN(Number(v)) || Number(v) < 0));

  const saveEdits = async () => {
    if (changedEdits.length === 0 || badEdit) return;
    const result = await dispatch(savePmPartWeights({ iwoNo, changedParts: changedEdits, userId }));
    if (result.success) {
      setEditOpen(false);
      setMessage({ severity: "success", text: `${result.updated} part weight(s) updated for IWO ${iwoNo}` });
    } else {
      setMessage({ severity: "error", text: result.message });
    }
    dispatch(fetchPmPartWeights(iwoNo));
  };

  const shownParts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return saved
      .filter((p) => !q || [p.PartNo, p.FrameNo, p.ComponentName].some((v) => String(v || "").toLowerCase().includes(q)))
      .sort((a, b) => String(a.PartNo).localeCompare(String(b.PartNo), undefined, { numeric: true }));
  }, [saved, search]);

  if (!iwo) return null;

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      style={{ display: "none" }}
      onChange={(e) => handleFile(e.target.files?.[0])}
    />
  );

  return (
    <Box sx={{ mb: 2 }}>
      {fileInput}

      {partWeightsLoading ? (
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: "14px", border: "1px solid #d6dce5", display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>Checking part weights for IWO {iwoNo}…</Typography>
        </Paper>
      ) : partWeightsError ? (
        <Alert severity="error" sx={{ borderRadius: "12px" }}>{partWeightsError}</Alert>
      ) : !hasParts ? (
        /* ---- First time: big upload box ---- */
        <Paper
          elevation={0}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "16px",
            border: `2px dashed ${dragOver ? BLUE : "#f0a500"}`,
            backgroundColor: dragOver ? "#eef4fb" : "#fffaf0",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
            <UploadFileIcon sx={{ fontSize: 40, color: "#e0a100" }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "15px" }}>
                Upload part list Excel for IWO {iwoNo}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                No part weights yet. Only needed once per IWO — the <b>Part</b> and <b>Weight</b> columns are read, other columns are ignored.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={reading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            onClick={() => fileRef.current?.click()}
            disabled={reading || partWeightsSaving}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", backgroundColor: "#e0a100", "&:hover": { backgroundColor: "#c78f00" }, whiteSpace: "nowrap" }}
          >
            {reading ? "Reading…" : "Choose Excel"}
          </Button>
        </Paper>
      ) : (
        /* ---- Already uploaded: small bar ---- */
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            px: 1.75,
            borderRadius: "14px",
            border: "1px solid #d6dce5",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <ScaleIcon sx={{ color: BLUE, fontSize: 20 }} />
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: NAVY }}>Part weights</Typography>
          <Chip size="small" label={`${saved.length} parts`} sx={{ height: 22, fontSize: "11px", backgroundColor: "#e8f0fa", color: "#0E3C66", fontWeight: 600 }} />
          <Chip size="small" label={fmtKg(totalWeight)} sx={{ height: 22, fontSize: "11px" }} />
          <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
            <Button size="small" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={openEdit} sx={{ textTransform: "none", fontWeight: 600 }}>
              View / Edit
            </Button>
            <Tooltip title="Upload the Excel again - only new parts and changed weights are saved">
              <span>
                <Button
                  size="small"
                  startIcon={reading ? <CircularProgress size={14} /> : <UploadFileIcon fontSize="small" />}
                  onClick={() => fileRef.current?.click()}
                  disabled={reading || partWeightsSaving}
                  sx={{ textTransform: "none", fontWeight: 600, color: "#6b7280" }}
                >
                  Re-upload Excel
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      )}

      {readError && <Alert severity="error" sx={{ mt: 1, borderRadius: "12px" }} onClose={() => setReadError("")}>{readError}</Alert>}
      {message && <Alert severity={message.severity} sx={{ mt: 1, borderRadius: "12px" }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {/* ---- Excel preview / confirm ---- */}
      <Dialog open={!!preview} onClose={partWeightsSaving ? undefined : () => setPreview(null)} fullScreen={isMobile} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}>
        <DialogTitle sx={{ color: NAVY, fontWeight: 700, display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          {hasParts ? "Re-upload part list" : "Upload part list"} · IWO {iwoNo}
          <IconButton onClick={() => setPreview(null)} disabled={partWeightsSaving} sx={{ ml: "auto" }} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {preview && (
          <DialogContent dividers>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, wordBreak: "break-all" }}>
              {preview.fileName} · sheet "{preview.sheet}" · {preview.parts.length} parts · {fmtKg(preview.parts.reduce((n, p) => n + p.weight, 0))}
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
              {[
                { label: "New", value: preview.newParts.length, color: "#2e7d32", bg: "#eaf7ee" },
                { label: "Changed", value: preview.changedParts.length, color: "#d97706", bg: "#fff6e5" },
                { label: "No change", value: preview.unchanged, color: "#6b7280", bg: "#f3f6fa" },
              ].map((c) => (
                <Box key={c.label} sx={{ p: 1, borderRadius: "10px", backgroundColor: c.bg, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{c.label}</Typography>
                  <Typography sx={{ fontSize: "18px", fontWeight: 700, color: c.color }}>{c.value}</Typography>
                </Box>
              ))}
            </Box>

            {preview.duplicates.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5, borderRadius: "10px" }}>
                Part {preview.duplicates.join(", ")} is in the Excel more than once — the first row's weight is used.
              </Alert>
            )}
            {preview.skipped > 0 && (
              <Alert severity="info" sx={{ mb: 1.5, borderRadius: "10px" }}>
                {preview.skipped} row(s) have no weight and are skipped.
              </Alert>
            )}
            {hasParts && preview.missing > 0 && (
              <Alert severity="info" sx={{ mb: 1.5, borderRadius: "10px" }}>
                {preview.missing} saved part(s) are not in this Excel — they are kept as they are.
              </Alert>
            )}

            <PreviewList title="Changed weights" color="#d97706" rows={preview.changedParts} showOld />
            <PreviewList title={hasParts ? "New parts" : "Parts"} color="#2e7d32" rows={preview.newParts} />

            {preview.newParts.length === 0 && preview.changedParts.length === 0 && (
              <Typography sx={{ fontSize: "13px", color: "#6b7280", textAlign: "center", py: 3 }}>
                Nothing to save — every part and weight is already the same.
              </Typography>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setPreview(null)} disabled={partWeightsSaving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={savePreview}
            disabled={partWeightsSaving || !preview || (preview.newParts.length === 0 && preview.changedParts.length === 0)}
            startIcon={partWeightsSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", backgroundColor: NAVY }}
          >
            {preview && hasParts ? `Save ${preview.newParts.length + preview.changedParts.length} change(s)` : `Save ${preview ? preview.newParts.length : 0} parts`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- View / edit saved weights ---- */}
      <Dialog open={editOpen} onClose={partWeightsSaving ? undefined : () => setEditOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}>
        <DialogTitle sx={{ color: NAVY, fontWeight: 700, display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          Part weights · IWO {iwoNo}
          <IconButton onClick={() => setEditOpen(false)} disabled={partWeightsSaving} sx={{ ml: "auto" }} aria-label="Close part weights">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            fullWidth
            placeholder="Search part no, frame or component"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
          <Box sx={{ maxHeight: isMobile ? "none" : "55vh", overflow: "auto", border: "1px solid #eef1f5", borderRadius: "10px" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontFamily: (t) => t.typography.fontFamily }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={th}>Part</Box>
                  <Box component="th" sx={th}>Frame</Box>
                  <Box component="th" sx={{ ...th, textAlign: "right", width: 150 }}>Weight (kg)</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {shownParts.map((p) => {
                  const value = edits[p.PartNo] !== undefined ? edits[p.PartNo] : String(p.Weight ?? "");
                  const changed = edits[p.PartNo] !== undefined && edits[p.PartNo] !== "" && !sameWeight(edits[p.PartNo], p.Weight);
                  const bad = edits[p.PartNo] !== undefined && edits[p.PartNo] !== "" && (isNaN(Number(edits[p.PartNo])) || Number(edits[p.PartNo]) < 0);
                  return (
                    <Box component="tr" key={p.PartId || p.PartNo} sx={{ backgroundColor: changed ? "#fff8e6" : "transparent" }}>
                      <Box component="td" sx={{ ...td, fontWeight: 600 }}>{p.PartNo}</Box>
                      <Box component="td" sx={{ ...td, fontSize: "12px", color: p.FrameNo ? "#333" : "#9aa3b2" }}>{p.FrameNo || "—"}</Box>
                      <Box component="td" sx={{ ...td, py: "3px" }}>
                        <TextField
                          size="small"
                          value={value}
                          error={bad}
                          onChange={(e) => {
                            const t = e.target.value.replace(/[^\d.]/g, "");
                            setEdits((prev) => ({ ...prev, [p.PartNo]: t }));
                          }}
                          inputProps={{ inputMode: "decimal", "aria-label": `Weight of part ${p.PartNo}`, style: { textAlign: "right", padding: "5px 8px" } }}
                          InputProps={{
                            endAdornment: changed ? (
                              <Tooltip title={`Undo (was ${p.Weight})`}>
                                <IconButton size="small" edge="end" onClick={() => setEdits((prev) => { const n = { ...prev }; delete n[p.PartNo]; return n; })}>
                                  <UndoIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null,
                          }}
                          sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" } }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            {shownParts.length === 0 && (
              <Typography sx={{ fontSize: "13px", color: "#9aa3b2", textAlign: "center", py: 3 }}>No parts match your search</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mr: "auto" }}>
            {changedEdits.length > 0 ? `${changedEdits.length} change(s)` : `${saved.length} parts`}
          </Typography>
          <Button onClick={() => setEditOpen(false)} disabled={partWeightsSaving} sx={{ textTransform: "none" }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={saveEdits}
            disabled={partWeightsSaving || changedEdits.length === 0 || badEdit}
            startIcon={partWeightsSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", backgroundColor: NAVY }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartWeights;